import { useEffect, useMemo, useRef, useState } from "react";
import PageUploadAudioAPIService from "../../service/PageUploadAudioAPIService";
import { AppDBConfigUID, BluejayNodeStatusStep } from "../../util/Constant";

/** One pipeline node entry from the `/audio/progress` status `steps` map. */
interface StatusStepEntry {
  id?: string;
  status?: string;
}

/** Some steps (analyze_qc, recommendation nodes) are keyed by config uid. */
type StatusStepValue = StatusStepEntry | Record<string, StatusStepEntry>;
export type StatusStepsMap = Partial<Record<string, StatusStepValue>>;

interface AudioProgressResponse {
  status?: string;
  fileId?: string;
  fileName?: string;
  url?: string;
  preprocessedUrl?: string;
  steps?: StatusStepsMap;
}

export type StepUiState = "completed" | "error" | "in_progress" | "pending";

/**
 * Overall workflow lifecycle derived from the individual step states.
 * - `pending`: every step is still pending (nothing started)
 * - `running`: at least one step is in progress / partially done
 * - `succeeded`: every required step completed
 * - `failed`: at least one step failed
 * - `idle`: nothing is being tracked
 */
export type WorkflowStatus = "idle" | "pending" | "running" | "succeeded" | "failed";

interface PipelineStepDescriptor {
  key: BluejayNodeStatusStep;
  label: string;
  /** When set, the step value is nested under this config uid. */
  configUid?: string;
}

export interface PipelineStepProgress {
  key: BluejayNodeStatusStep;
  label: string;
  state: StepUiState;
}

/**
 * Ordered pipeline rendered as a checklist while the workflow runs.
 * All entries must reach "Completed" before the analysis is ready.
 *
 * The Odia recommendation translation steps below are commented out: the workflow
 * currently produces the recommendation and its summary in English only. Once the
 * backend resumes producing the Odia translations, uncomment them to require those too.
 */
const PIPELINE_STEPS: PipelineStepDescriptor[] = [
  { key: BluejayNodeStatusStep.PREPROCESSING, label: "Preprocessing audio" },
  { key: BluejayNodeStatusStep.TRANSCRIBE_LOCAL_LANG, label: "Transcribing" },
  { key: BluejayNodeStatusStep.TRANSLATE_ENGLISH_LANG, label: "Translating to English" },
  {
    key: BluejayNodeStatusStep.ANALYZE_QC,
    label: "Analyzing transcript",
    configUid: AppDBConfigUID.BLUEJAY_ANALYSIS_CONFIG_UID,
  },
  {
    key: BluejayNodeStatusStep.GENERATE_RECOMMENDATION_ENGLISH_LANG,
    label: "Generating PD Report (English)",
    configUid: AppDBConfigUID.BLUEJAY_RECO_CONFIG_UID,
  },
  {
    key: BluejayNodeStatusStep.GENERATE_RECOMMENDATION_SUMMARY_ENGLISH_LANG,
    label: "Generating PD Report summary (English)",
    configUid: AppDBConfigUID.BLUEJAY_RECO_SUMMARY_CONFIG_UID,
  },
  // {
  //   key: BluejayNodeStatusStep.TRANSLATE_RECOMMENDATION_LOCAL_LANG,
  //   label: "Translating recommendation (Odia)",
  //   configUid: AppDBConfigUID.BLUEJAY_RECO_CONFIG_UID,
  // },
  // {
  //   key: BluejayNodeStatusStep.TRANSLATE_RECOMMENDATION_SUMMARY_LOCAL_LANG,
  //   label: "Translating recommendation summary (Odia)",
  //   configUid: AppDBConfigUID.BLUEJAY_RECO_SUMMARY_CONFIG_UID,
  // },
];

export const REQUIRED_COMPLETED_STEPS = PIPELINE_STEPS.length;

/** Interval between successive `/audio/progress` polls while a workflow is running. */
const POLL_INTERVAL_MS = 3000;

/**
 * Read a single step's raw status from the steps map, resolving nested config-keyed entries.
 *
 * Config-keyed steps don't always sit under the config uid we know: records from
 * `/workflows/status` (the Recent Audio panel) may carry a flat `{status}` entry, or be
 * nested under a different config uid than the current constants. Fall back through those
 * shapes so completed steps still count instead of reading as forever-pending.
 */
const getStepStatus = (steps: StatusStepsMap, key: string, configUid?: string): string | null => {
  const value = steps?.[key];
  if (!value) return null;
  const direct = (value as StatusStepEntry).status;
  if (configUid) {
    const nestedMap = value as Record<string, StatusStepEntry>;
    const nested = nestedMap[configUid];
    if (nested?.status) return nested.status;
    if (typeof direct === "string") return direct;
    const firstNested = Object.values(nestedMap).find(
      (entry) => entry && typeof entry === "object" && typeof entry.status === "string"
    );
    return firstNested?.status ?? null;
  }
  return direct ?? null;
};

/** Status strings arrive capitalized (e.g. "Completed"); normalize for UI rendering. */
const normalizeStepState = (status: string | null): StepUiState => {
  if (!status) return "pending";
  const normalized = status.toLowerCase();
  if (["completed", "complete", "success", "succeeded", "done"].includes(normalized)) {
    return "completed";
  }
  if (["error", "failed", "failure"].includes(normalized)) {
    return "error";
  }
  return "in_progress";
};

export const STEP_UI: Record<StepUiState, { row: string; badge: string; icon: string }> = {
  completed: { row: "border-green-200 bg-green-50 text-green-700", badge: "Completed", icon: "✓" },
  error: { row: "border-red-200 bg-red-50 text-red-700", badge: "Error", icon: "✗" },
  in_progress: {
    row: "border-blue-200 bg-blue-50 text-blue-700",
    badge: "In progress",
    icon: "⏳",
  },
  pending: { row: "border-gray-200 bg-gray-50 text-gray-500", badge: "Pending", icon: "•" },
};

/** Presentation for the overall workflow status (badge label + chip classes). */
export const WORKFLOW_STATUS_UI: Record<
  Exclude<WorkflowStatus, "idle">,
  { label: string; chip: string }
> = {
  pending: { label: "Pending", chip: "bg-gray-100 text-gray-600" },
  running: { label: "In Progress", chip: "bg-blue-100 text-blue-700" },
  succeeded: { label: "Success", chip: "bg-green-100 text-green-700" },
  failed: { label: "Failed", chip: "bg-red-100 text-red-700" },
};

export interface WorkflowStatusSummary {
  /** Per-step checklist progress, ordered for rendering. */
  pipelineProgress: PipelineStepProgress[];
  /** Count of steps that have reached "Completed". */
  completedCount: number;
  /** True when at least one step has failed. */
  hasError: boolean;
  /** True when every required step has completed. */
  isComplete: boolean;
  /** Overall lifecycle of the workflow (never "idle" — a steps map is always a tracked workflow). */
  status: Exclude<WorkflowStatus, "idle">;
}

/**
 * Pure derivation of per-step progress and overall status from a raw `/audio/progress`
 * (or `/workflows/status`) steps map. Shared by the live tracker hook and any list that
 * needs to label a workflow record (e.g. the Recent Audio panel).
 */
export function deriveWorkflowStatus(steps: StatusStepsMap): WorkflowStatusSummary {
  const pipelineProgress: PipelineStepProgress[] = PIPELINE_STEPS.map((step) => ({
    key: step.key,
    label: step.label,
    state: normalizeStepState(getStepStatus(steps, step.key, step.configUid)),
  }));

  const completedCount = pipelineProgress.filter((step) => step.state === "completed").length;
  const hasError = pipelineProgress.some((step) => step.state === "error");
  const isComplete = completedCount >= REQUIRED_COMPLETED_STEPS;
  const allPending = pipelineProgress.every((step) => step.state === "pending");

  let status: WorkflowStatusSummary["status"];
  if (hasError) status = "failed";
  else if (isComplete) status = "succeeded";
  else if (allPending) status = "pending";
  else status = "running";

  return { pipelineProgress, completedCount, hasError, isComplete, status };
}

export interface AudioWorkflowStatus {
  /** Per-step checklist progress, ordered for rendering. */
  pipelineProgress: PipelineStepProgress[];
  /** Count of steps that have reached "Completed". */
  completedCount: number;
  /** True while a poll request is in flight. */
  progressLoading: boolean;
  /** File id reported by the workflow once the file record exists. */
  fileId: string;
  /** Preprocessed audio url reported by the workflow. */
  processedUrl: string;
  /** True when at least one step has failed. */
  hasError: boolean;
  /** True when every required step has completed. */
  isComplete: boolean;
  /** Overall lifecycle of the workflow. */
  status: WorkflowStatus;
}

/**
 * Long-polls the `/audio/progress` endpoint for a given status id (the audio url that
 * identifies a triggered workflow) and derives the workflow's overall state.
 *
 * Polling stops automatically once every required step has completed or any step fails,
 * so the same hook can drive both the post-upload flow and a `statusId`-resumed page.
 *
 * @param statusId The audio url identifying the workflow. Empty disables polling.
 */
export default function useAudioWorkflowStatus(statusId: string): AudioWorkflowStatus {
  const [steps, setSteps] = useState<StatusStepsMap>({});
  const [progressLoading, setProgressLoading] = useState(false);
  const [fileId, setFileId] = useState("");
  const [processedUrl, setProcessedUrl] = useState("");
  const stoppedRef = useRef(false);

  useEffect(() => {
    // Reset derived state whenever the workflow we're tracking changes.
    setSteps({});
    setFileId("");
    setProcessedUrl("");
    stoppedRef.current = false;

    if (!statusId) {
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const fetchProgress = async () => {
      if (cancelled) return;
      try {
        setProgressLoading(true);
        const progress: AudioProgressResponse | undefined =
          await PageUploadAudioAPIService.getAudioProgress(statusId);
        if (cancelled || !progress) return;
        if (progress.fileId) {
          // The upload endpoint no longer returns a file id; it appears here once
          // the workflow creates the file record.
          setFileId(progress.fileId);
        }
        setProcessedUrl(progress.preprocessedUrl || "");
        const nextSteps = progress.steps || {};
        setSteps(nextSteps);

        const states = PIPELINE_STEPS.map((step) =>
          normalizeStepState(getStepStatus(nextSteps, step.key, step.configUid))
        );
        const completed = states.filter((s) => s === "completed").length;
        const failed = states.some((s) => s === "error");

        // Stop long-polling once the workflow has succeeded or failed.
        if (completed >= REQUIRED_COMPLETED_STEPS || failed) {
          stoppedRef.current = true;
          stopPolling();
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error polling audio progress:", error);
        }
      } finally {
        if (!cancelled) {
          setProgressLoading(false);
        }
      }
    };

    fetchProgress();
    intervalId = setInterval(fetchProgress, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [statusId]);

  const summary = useMemo(() => deriveWorkflowStatus(steps), [steps]);

  // Without a tracked workflow there is nothing to report.
  const status: WorkflowStatus = statusId ? summary.status : "idle";

  return {
    ...summary,
    progressLoading,
    fileId,
    processedUrl,
    status,
  };
}
